import type { CombatSeatInput } from "./combat-types.js";
import { unitRule } from "./economy-rules.js";
import {
  addRational,
  multiplyRational,
  type Rational,
  rational,
  rationalFromDecimal,
} from "./exact-numbers.js";
import type { UNIT_IDS } from "./logistics-types.js";
import { effectMultiplier } from "./research-effects.js";

const ZERO = rational(0n);
const ONE = rational(1n);

export interface PacketDamage {
  readonly troopPayload: Rational;
  readonly infrastructurePayload: Rational;
  readonly troopShield: Rational;
  readonly infrastructureShield: Rational;
}

export function unitPacketDamage(
  id: (typeof UNIT_IDS)[number],
  encodedStrength: string,
  order: "hold" | "assault" | "raid",
  hasDefenders: boolean,
  shieldWasEmpty: boolean,
  supply: Rational,
  duration: Rational,
  attacker: CombatSeatInput,
): PacketDamage {
  const strength = rationalFromDecimal(encodedStrength);
  if (strength.numerator === 0n) return emptyPacketDamage();
  const unit = unitRule(id);
  const prefix = id === "guard-robot" ? "guard" : id === "raider-robot" ? "raider" : "siege";
  const modifier = (suffix: string) =>
    effectMultiplier(attacker.researchEffects, `${prefix}-${suffix}`);
  const troopRate = multiplyRational(
    rational(BigInt(unit.troopDamagePerSecond)),
    rationalFromDecimal(modifier("troop-damage")),
  );
  const buildingRate = multiplyRational(
    rational(BigInt(unit.infrastructureDamagePerSecond)),
    rationalFromDecimal(modifier("building-damage")),
  );
  const shieldRate = multiplyRational(
    rational(BigInt(hasDefenders ? unit.troopDamagePerSecond : unit.infrastructureDamagePerSecond)),
    rationalFromDecimal(modifier("shield-damage")),
  );
  const base = multiplyRational(multiplyRational(strength, supply), duration);
  if (!hasDefenders)
    return {
      ...emptyPacketDamage(),
      infrastructurePayload: multiplyRational(base, buildingRate),
      infrastructureShield: multiplyRational(base, shieldRate),
    };
  if (order === "raid" && id === "raider-robot" && shieldWasEmpty) {
    const leak = rationalFromDecimal(
      attacker.researchEffects.ratios["raider-infrastructure-leak"] ?? "0.2",
    );
    return {
      ...emptyPacketDamage(),
      troopPayload: multiplyRational(multiplyRational(base, troopRate), subtract(ONE, leak)),
      infrastructurePayload: multiplyRational(multiplyRational(base, buildingRate), leak),
    };
  }
  return {
    ...emptyPacketDamage(),
    troopPayload: multiplyRational(base, troopRate),
    troopShield: multiplyRational(base, shieldRate),
  };
}

function emptyPacketDamage(): PacketDamage {
  return {
    troopPayload: ZERO,
    infrastructurePayload: ZERO,
    troopShield: ZERO,
    infrastructureShield: ZERO,
  };
}

function subtract(left: Rational, right: Rational): Rational {
  return addRational(left, rational(-right.numerator, right.denominator));
}
